import React, { useState } from "react";
import { Phone, Clock, CalendarCheck } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { submitLead } from "../../lib/lead";

const COPY = {
  buy: {
    eyebrow: "Complimentary 15-Minute Strategy Call",
    title: "Let\u2019s Map Out Your Next Move",
    body: "Before you tour a single home, sit down with our team for a relaxed 15-minute call. We\u2019ll talk through your goals, budget, timeline and must-haves \u2014 and show you exactly how we\u2019ll find, negotiate and win the right home for you.",
    points: ["Clarify budget & financing options", "Pinpoint the right DMV neighborhoods", "Get early access to off-market & coming-soon homes"],
    source: "Buyer Consultation (15 min)",
  },
  sell: {
    eyebrow: "Complimentary 15-Minute Selling Strategy Call",
    title: "Let\u2019s Talk About Your Home",
    body: "Start with a no-pressure 15-minute call with our team. We\u2019ll learn about your home and your timeline, share what similar homes are selling for, and walk you through the cinematic marketing plan that sells our listings in days, not months.",
    points: ["What your home could sell for today", "Timing, prep & staging recommendations", "Our signature film, media & broker-open launch plan"],
    source: "Seller Consultation (15 min)",
  },
};

const TIMES = ["Morning", "Afternoon", "Evening"];
const field = "w-full bg-transparent border-0 border-b border-white/25 text-white placeholder:text-white/40 py-3 text-[15px] focus:outline-none focus:border-[#b4936a] transition-colors";

const ConsultationCTA = ({ type = "buy" }) => {
  const c = COPY[type];
  const [form, setForm] = useState({ name: "", phone: "", email: "", time: "Morning" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Please complete all fields", description: "Name, phone and email help us reach you." });
      return;
    }
    setLoading(true);
    try {
      const [firstName, ...rest] = form.name.trim().split(" ");
      await submitLead({ firstName, lastName: rest.join(" "), phone: form.phone, email: form.email, source: c.source, details: { "Preferred Time": form.time } });
      toast({ title: "Your call is requested", description: "We\u2019ll reach out shortly to confirm a time that works for you." });
      setForm({ name: "", phone: "", email: "", time: "Morning" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consultation" data-testid={`consultation-${type}`} className="bg-[#0d0d0d] text-white py-24 md:py-32">
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-20 items-center">
        <div className="reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-5 flex items-center gap-2"><Clock size={14} /> {c.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-tight mb-6">{c.title}</h2>
          <p className="text-white/60 text-[15px] leading-relaxed max-w-xl mb-8">{c.body}</p>
          <ul className="space-y-3">
            {c.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-white/80 text-[15px]"><span className="w-1.5 h-1.5 bg-[#b4936a] rounded-full" /> {p}</li>
            ))}
          </ul>
        </div>
        <form onSubmit={submit} className="reveal-scale bg-white/[0.04] border border-white/10 backdrop-blur-md p-8 md:p-10 space-y-6">
          <p className="font-serif-display text-2xl flex items-center gap-3"><CalendarCheck size={22} className="text-[#b4936a]" /> Book Your 15-Minute Call</p>
          <input data-testid={`consult-${type}-name`} placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
          <input data-testid={`consult-${type}-phone`} placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
          <input data-testid={`consult-${type}-email`} type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-white/50 mb-3">Best time to call</p>
            <div className="flex gap-2">
              {TIMES.map((t) => (
                <button type="button" key={t} data-testid={`consult-${type}-time-${t.toLowerCase()}`} onClick={() => setForm({ ...form, time: t })} className={`flex-1 text-[10px] sm:text-[11px] tracking-wide sm:tracking-luxe-sm uppercase py-3 px-1 border transition-colors ${form.time === t ? "bg-[#b4936a] border-[#b4936a] text-white" : "border-white/20 text-white/70 hover:border-[#b4936a]"}`}>{t}</button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid={`consult-${type}-submit`} className="w-full bg-white text-[#0d0d0d] text-[11px] tracking-luxe uppercase py-4 hover:bg-[#b4936a] hover:text-white transition-colors disabled:opacity-60">
            {loading ? "Sending\u2026" : "Request My Call"}
          </button>
          <p className="text-[11px] text-white/40 flex items-center gap-2"><Phone size={12} /> Prefer to talk now? Call (443) 955-8400</p>
        </form>
      </div>
    </section>
  );
};

export default ConsultationCTA;
